import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../../app/hooks'
import { uploadMultimediaAttachment } from '../../../../api/multimedia/multimediaApi'
import { showMessage } from '../../../../redux/reducers/message/messageFeature'
import { AiUploadedImage } from './AiUploadImageSection'
import { UploadedAttachmentState } from './aiDataExtractionRequest'
import { MAX_IMAGE_FILE_SIZE_BYTES } from './aiUploadedAttachmentsConstants'
import {
  asAttachmentUploadState,
  createUploadedImage,
  getSelectedImagesFromFileList,
  hasAnyFileExceedingMaxSize,
} from './aiUploadedAttachmentsPure'
import { registerInFlightUpload } from './inFlightUploadRegistry'
import { persistMultimediaAttachmentToStore } from './persistMultimediaAttachmentToStore'

type UseAiImageAttachmentsParams = {
  pendingUploadPromisesRef: MutableRefObject<Record<string, Promise<void>>>
}

export const useAiImageAttachments = ({ pendingUploadPromisesRef }: UseAiImageAttachmentsParams) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [images, setImages] = useState<AiUploadedImage[]>([])
  const [imageErrorText, setImageErrorText] = useState<string | undefined>()
  const [uploadedImageAttachments, setUploadedImageAttachments] = useState<Record<string, UploadedAttachmentState>>({})

  const imagesRef = useRef<AiUploadedImage[]>([])
  const uploadedImageAttachmentsRef = useRef<Record<string, UploadedAttachmentState>>({})

  useEffect(() => {
    imagesRef.current = images
  }, [images])

  useEffect(() => {
    uploadedImageAttachmentsRef.current = uploadedImageAttachments
  }, [uploadedImageAttachments])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- blob teardown reads latest mutable refs at unmount
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl))
    }
  }, [])

  const setUploadedImageAttachmentEntry = useCallback((imageId: string, attachmentState: UploadedAttachmentState) => {
    setUploadedImageAttachments((currentAttachments) => ({
      ...currentAttachments,
      [imageId]: attachmentState,
    }))
  }, [])

  const uploadImageAndSyncAttachmentState = useCallback(
    async (image: AiUploadedImage) => {
      const imageId = image.id
      const isStillSelected = () => imagesRef.current.some((img) => img.id === imageId)

      try {
        const attachment = await uploadMultimediaAttachment(image.file)
        if (!isStillSelected()) {
          return
        }
        const normalizedAttachment = persistMultimediaAttachmentToStore(dispatch, attachment, image.name)
        setUploadedImageAttachmentEntry(imageId, asAttachmentUploadState('uploaded', normalizedAttachment))
      } catch {
        if (!isStillSelected()) {
          return
        }
        dispatch(showMessage({ message: 'unknown' }))
        setUploadedImageAttachmentEntry(imageId, asAttachmentUploadState('failed'))
      }
    },
    [dispatch, setUploadedImageAttachmentEntry]
  )

  const onImageSelect = (files: FileList | null) => {
    if (!files) {
      return
    }

    const selectedImages = getSelectedImagesFromFileList(files)
    const oversizedImageSelected = hasAnyFileExceedingMaxSize(selectedImages, MAX_IMAGE_FILE_SIZE_BYTES)

    setImageErrorText(oversizedImageSelected ? t('wizard.uploadAiBanner.modal.photos.maxFileSizeError') : undefined)

    const uploadedImages = selectedImages
      .filter((file) => file.size <= MAX_IMAGE_FILE_SIZE_BYTES)
      .map(createUploadedImage)

    if (uploadedImages.length > 0) {
      const mergedImages = [...imagesRef.current, ...uploadedImages]
      imagesRef.current = mergedImages
      setImages(mergedImages)
      setUploadedImageAttachments((currentAttachments) => ({
        ...currentAttachments,
        ...Object.fromEntries(uploadedImages.map((image) => [image.id, asAttachmentUploadState('uploading')])),
      }))
      uploadedImages.forEach((image) => {
        registerInFlightUpload(pendingUploadPromisesRef, image.id, uploadImageAndSyncAttachmentState(image))
      })
    }
  }

  const onImageRemove = (imageId: string) => {
    const imageToRemove = imagesRef.current.find((image) => image.id === imageId)
    if (imageToRemove) {
      URL.revokeObjectURL(imageToRemove.previewUrl)
    }

    imagesRef.current = imagesRef.current.filter((image) => image.id !== imageId)
    setImages((currentImages) => currentImages.filter((image) => image.id !== imageId))
    setUploadedImageAttachments((currentAttachments) => {
      const { [imageId]: _removedAttachment, ...remainingAttachments } = currentAttachments
      return remainingAttachments
    })
  }

  const onImageReorder = (imageIdsInOrder: string[]) => {
    const imagesById = new Map(imagesRef.current.map((image) => [image.id, image]))
    const reordered = imageIdsInOrder
      .map((imageId) => imagesById.get(imageId))
      .filter((image): image is AiUploadedImage => Boolean(image))
    imagesRef.current = reordered
    setImages(reordered)
  }

  return {
    imageErrorText,
    images,
    imagesRef,
    onImageRemove,
    onImageReorder,
    onImageSelect,
    uploadedImageAttachmentsRef,
  }
}
